import React, { useState } from "react";
import Icon from "../../../../components/AppIcon";

const JobRoleSyncModal = ({ providers, onClose, onSync }) => {
  const [selectedProvider, setSelectedProvider] = useState(providers[0] || "");
  const [syncing, setSyncing] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleSync = async () => {
    if (!selectedProvider || !confirmed) return;

    setSyncing(true);
    try {
      await onSync(selectedProvider);
    } finally {
      setSyncing(false);
    }
  };

  const providerInfo = {
    Sona: {
      icon: "Cloud",
      description: "Sync job roles from Sona HR system",
      color: "#3b82f6",
    },
    BambooHR: {
      icon: "Leaf",
      description: "Sync job roles from BambooHR",
      color: "#10b981",
    },
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-card rounded-xl shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Icon name="RefreshCw" size={20} />
            Sync Job Roles
          </h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-muted-foreground">
            Sync job roles from your HR system. Existing roles will be updated
            and new roles will be created.
          </p>

          {/* Provider Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Select HR Provider
            </label>
            <div className="space-y-2">
              {providers.map((provider) => {
                const info = providerInfo[provider] || {
                  icon: "Link",
                  description: `Sync from ${provider}`,
                  color: "#6b7280",
                };
                return (
                  <button
                    key={provider}
                    onClick={() => setSelectedProvider(provider)}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left flex items-center gap-3 ${
                      selectedProvider === provider
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-muted-foreground/50"
                    }`}
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${info.color}20` }}
                    >
                      <Icon
                        name={info.icon}
                        size={20}
                        style={{ color: info.color }}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{provider}</p>
                      <p className="text-sm text-muted-foreground">
                        {info.description}
                      </p>
                    </div>
                    {selectedProvider === provider && (
                      <Icon
                        name="CheckCircle"
                        size={20}
                        className="text-primary"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sync Behavior Info */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Icon name="Info" size={16} />
              What happens during sync?
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Icon name="Check" size={14} className="mt-0.5 text-success" />
                <span>New roles from the HR system will be created</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="Check" size={14} className="mt-0.5 text-success" />
                <span>Existing roles will be updated with HR data</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="Check" size={14} className="mt-0.5 text-success" />
                <span>
                  Roles with matching names will be linked automatically
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="Shield" size={14} className="mt-0.5 text-warning" />
                <span>Locally modified roles will not be overwritten</span>
              </li>
            </ul>
          </div>

          {/* Confirmation */}
          <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="checkbox mt-0.5"
            />
            <span className="text-sm">
              I understand that this will sync job roles from{" "}
              {selectedProvider || "the selected HR provider"} and may update
              existing roles.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
          <button
            onClick={onClose}
            className="btn btn-outline"
            disabled={syncing}
          >
            Cancel
          </button>
          <button
            onClick={handleSync}
            className="btn btn-primary"
            disabled={!selectedProvider || !confirmed || syncing}
          >
            {syncing ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <Icon name="RefreshCw" size={16} />
                Start Sync
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobRoleSyncModal;
